import { useState } from 'react';
import type { StakeScenarioCell, StakeScenarioWinType } from '@sr-test-wizard/shared';
import { useStakeScenariosStore } from '../../state/stakeScenariosStore';
import { MarkdownView } from '../extras-game-kb/ReportView';

function winTypeClass(w: StakeScenarioWinType): string {
  if (w === 'wincap') return 'bad';
  if (w === 'win') return 'good';
  if (w === 'sub') return 'warn';
  return 'muted';
}

const SOURCE_TAG: Record<string, { label: string; cls: string }> = {
  bespoke: { label: 'Per-round AI deep dive', cls: 'good' },
  template: { label: 'Templated (shape-shared)', cls: 'warn' },
  deterministic: { label: 'Factual summary (AI withheld for precision)', cls: 'muted' },
};

function Cell({ cell }: { cell: StakeScenarioCell }) {
  const badges: string[] = [];
  if (cell.multiplier != null) badges.push(`x${cell.multiplier}`);
  if (cell.bomb) badges.push('B');
  if (cell.scatter) badges.push('S');
  const special = cell.bomb || cell.scatter || cell.multiplier != null;
  return (
    <div className={`scn-cell${special ? ' special' : ''}`}>
      <span className="scn-cell-name">{cell.name}</span>
      {badges.length ? <span className="scn-cell-badge">{badges.join(' ')}</span> : null}
    </div>
  );
}

/** Board comes in as reels x rows; render reels as columns. */
function Board({ board }: { board: StakeScenarioCell[][] }) {
  return (
    <div className="scn-board">
      {board.map((reel, ri) => (
        <div className="scn-reel" key={ri}>
          {reel.map((cell, ci) => (
            <Cell key={ci} cell={cell} />
          ))}
        </div>
      ))}
    </div>
  );
}

export function ScenarioDetail() {
  const s = useStakeScenariosStore();
  const [showRaw, setShowRaw] = useState(false);
  const detail = s.detail;
  const labels = s.meta?.profile.labels ?? {};
  const labelOf = (key: string) => labels[key] ?? key;

  return (
    <div className="scn-drawer-overlay" onClick={() => s.closeRecord()}>
      <aside className="scn-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="row" style={{ alignItems: 'center', gap: 8 }}>
          <h3 style={{ margin: 0 }}>
            {s.detailLoading ? 'Loading…' : `Round #${detail?.summary.id}`}
          </h3>
          <span className="spacer" />
          <button type="button" className="btn small" onClick={() => s.closeRecord()}>Close ✕</button>
        </div>

        {s.detailLoading || !detail ? (
          <p className="muted small" style={{ marginTop: 12 }}>Fetching record from the book…</p>
        ) : (
          <>
            <div className="scn-detail-head" style={{ marginTop: 8 }}>
              <span className={`chip ${winTypeClass(detail.summary.winType)}`}>{detail.summary.winType}</span>
              <span className="mono">{detail.summary.multiplier.toLocaleString()}x</span>
              <span className="small muted">criteria {detail.summary.criteria}</span>
              {detail.summary.oneIn != null && (
                <span className="small muted">· 1 in {Math.round(detail.summary.oneIn).toLocaleString()}</span>
              )}
              {Object.entries(detail.summary.attrs).map(([k, v]) => (
                <span key={k} className="chip muted small">{labelOf(k)}: {v}</span>
              ))}
              {detail.summary.features.map((k) => (
                <span key={k} className="chip good small">{labelOf(k)}</span>
              ))}
            </div>

            {detail.summary.board && (
              <>
                <h4 className="section-label">Board</h4>
                <Board board={detail.summary.board} />
              </>
            )}

            {detail.summary.paidSymbols.length > 0 && (
              <>
                <h4 className="section-label">Paid symbols</h4>
                <div className="small mono">
                  {detail.summary.paidSymbols.map((p, i) => (
                    <span key={i} className="scn-paid">{p.symbol}: {p.win}x</span>
                  ))}
                </div>
              </>
            )}

            <h4 className="section-label">Event timeline</h4>
            <ol className="scn-timeline">
              {detail.summary.steps.map((st, i) => (
                <li key={i}>
                  <span className="scn-step-label">{st.label}</span>
                  <span className="scn-step-type mono small">{st.type}</span>
                  {st.detail && <span className="scn-step-detail small muted">{st.detail}</span>}
                </li>
              ))}
            </ol>

            <div className="ai-analysis">
              <div className="ai-analysis-head">
                <h4 className="section-label" style={{ margin: 0 }}>AI: what to expect</h4>
                <span className="spacer" />
                <button
                  type="button"
                  className="btn small"
                  disabled={s.detailExplaining}
                  onClick={() => void s.explainRecord(detail.summary.id)}
                >
                  {s.detailExplaining ? 'Explaining…' : 'Explain this exact round'}
                </button>
              </div>
              {detail.explanation ? (
                <>
                  {detail.explanationSource && SOURCE_TAG[detail.explanationSource] && (
                    <div style={{ margin: '6px 0' }}>
                      <span className={`chip small ${SOURCE_TAG[detail.explanationSource].cls}`}>
                        {SOURCE_TAG[detail.explanationSource].label}
                      </span>
                    </div>
                  )}
                  <div className="ai-analysis-body"><MarkdownView>{detail.explanation}</MarkdownView></div>
                </>
              ) : (
                <p className="muted small" style={{ marginTop: 8 }}>
                  No AI text yet. Run "Explain shapes with AI" once — it instantly fills in a
                  round-specific explanation for every record. Or use "Explain this exact round" for a
                  bespoke deep-dive.
                </p>
              )}
            </div>

            <button
              type="button"
              className="btn small"
              style={{ marginTop: 12 }}
              onClick={() => setShowRaw((v) => !v)}
            >
              {showRaw ? 'Hide raw JSON' : 'Show raw JSON'}
            </button>
            {showRaw && (
              <pre className="scn-raw">{JSON.stringify(detail.raw, null, 2)}</pre>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
